import React, { useState, useEffect, useRef } from 'react';
import {
  Phone,
  User,
  MapPin,
  FileText,
  CreditCard,
  Upload,
  Camera,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  X,
  Compass,
  FileCheck,
} from 'lucide-react';
import {
  ID_PROOF_TYPES,
  PURPOSE_OF_STAY_OPTIONS,
  CustomerKYCProfile,
  lookupCustomerByPhone,
  getDocumentSignedUrl,
} from '../../lib/kycUtils';

export interface GuestKYCSectionProps {
  tenantId: string;
  phone: string;
  onPhoneChange: (val: string) => void;
  guestName: string;
  onGuestNameChange: (val: string) => void;
  address: string;
  onAddressChange: (val: string) => void;
  idProofType: string;
  onIdProofTypeChange: (val: string) => void;
  idProofNumber: string;
  onIdProofNumberChange: (val: string) => void;
  purposeOfStay: string;
  onPurposeOfStayChange: (val: string) => void;
  selectedFile: File | null;
  onFileSelect: (file: File | null) => void;
  existingDocUrl: string | null;
  onClearExistingDoc?: () => void;
  matchedCustomer: CustomerKYCProfile | null;
  onCustomerMatched: (cust: CustomerKYCProfile | null) => void;
  isMandatoryForCheckIn?: boolean;
}

export function GuestKYCSection({
  tenantId,
  phone,
  onPhoneChange,
  guestName,
  onGuestNameChange,
  address,
  onAddressChange,
  idProofType,
  onIdProofTypeChange,
  idProofNumber,
  onIdProofNumberChange,
  purposeOfStay,
  onPurposeOfStayChange,
  selectedFile,
  onFileSelect,
  existingDocUrl,
  onClearExistingDoc,
  matchedCustomer,
  onCustomerMatched,
  isMandatoryForCheckIn = false,
}: GuestKYCSectionProps) {
  const [searching, setSearching] = useState(false);
  const [searchFeedback, setSearchFeedback] = useState<string | null>(null);
  const [docPreviewUrl, setDocPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Load preview for existing doc URL
  useEffect(() => {
    let isCancelled = false;
    if (existingDocUrl) {
      getDocumentSignedUrl(existingDocUrl).then((url) => {
        if (!isCancelled) {
          setDocPreviewUrl(url);
        }
      });
    } else if (selectedFile) {
      const objUrl = URL.createObjectURL(selectedFile);
      setDocPreviewUrl(objUrl);
      return () => {
        URL.revokeObjectURL(objUrl);
      };
    } else {
      setDocPreviewUrl(null);
    }
    return () => {
      isCancelled = true;
    };
  }, [existingDocUrl, selectedFile]);

  // Debounced phone auto-fill lookup (500ms)
  useEffect(() => {
    const clean = phone.trim().replace(/\D/g, '');
    if (clean.length < 10) {
      if (matchedCustomer) {
        onCustomerMatched(null);
        setSearchFeedback(null);
      }
      return;
    }

    setSearching(true);
    const timer = setTimeout(async () => {
      if (!tenantId) {
        setSearching(false);
        return;
      }
      try {
        const found = await lookupCustomerByPhone(tenantId, phone);
        if (found) {
          onCustomerMatched(found);
          setSearchFeedback(`Existing guest found: ${found.customer_name}`);
          // Auto-fill fields if not already modified
          if (found.customer_name) onGuestNameChange(found.customer_name);
          if (found.address) onAddressChange(found.address);
          if (found.id_proof_type) onIdProofTypeChange(found.id_proof_type);
          if (found.id_proof_number) onIdProofNumberChange(found.id_proof_number);
        } else {
          onCustomerMatched(null);
          setSearchFeedback('New guest (not in directory)');
        }
      } catch (err) {
        console.warn('Auto-fill search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [phone, tenantId]);

  const handleManualBlur = async () => {
    const clean = phone.trim().replace(/\D/g, '');
    if (clean.length >= 10 && !matchedCustomer && tenantId) {
      setSearching(true);
      const found = await lookupCustomerByPhone(tenantId, phone);
      setSearching(false);
      if (found) {
        onCustomerMatched(found);
        setSearchFeedback(`Existing guest found: ${found.customer_name}`);
        if (found.customer_name) onGuestNameChange(found.customer_name);
        if (found.address) onAddressChange(found.address);
        if (found.id_proof_type) onIdProofTypeChange(found.id_proof_type);
        if (found.id_proof_number) onIdProofNumberChange(found.id_proof_number);
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onFileSelect(e.target.files[0]);
    }
  };

  return (
    <div className="space-y-4 rounded-2xl bg-slate-50/80 dark:bg-slate-900/60 p-4 sm:p-5 border border-slate-200 dark:border-slate-800">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-950/80 text-purple-700 dark:text-purple-300 flex items-center justify-center font-bold text-sm">
            🪪
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <span>Guest KYC & Verification</span>
              {isMandatoryForCheckIn && (
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-300">
                  Mandatory for Check-in
                </span>
              )}
            </h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Universal customer record linked to Retail, Restaurant & Hospitality
            </p>
          </div>
        </div>

        {matchedCustomer && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/70 border border-emerald-200 dark:border-emerald-800 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 size={13} className="shrink-0 text-emerald-600" />
            <span>Existing Profile Auto-filled</span>
          </div>
        )}
      </div>

      {/* Row 1: Phone (with Auto-fill) & Guest Name */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Mobile Number */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            {searching ? (
              <span className="text-[10px] text-purple-600 dark:text-purple-400 flex items-center gap-1 font-medium animate-pulse">
                <RefreshCw size={10} className="animate-spin" /> Searching...
              </span>
            ) : searchFeedback ? (
              <span
                className={`text-[10px] font-semibold ${
                  matchedCustomer
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                {searchFeedback}
              </span>
            ) : null}
          </div>
          <div className="relative">
            <Phone
              size={15}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="tel"
              id="input-guest-phone"
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              onBlur={handleManualBlur}
              placeholder="e.g. 9876543210"
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm font-medium text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
              required
            />
          </div>
          <p className="text-[10px] text-slate-400 mt-1">
            Type 10 digits to auto-fetch previous guest profile and ID proof
          </p>
        </div>

        {/* Guest Name */}
        <div>
          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
            Guest Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <User
              size={15}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              id="input-guest-name"
              value={guestName}
              onChange={(e) => onGuestNameChange(e.target.value)}
              placeholder="e.g. Rahul Sharma"
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm font-medium text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
              required
            />
          </div>
        </div>
      </div>

      {/* Row 2: Address (Textarea) */}
      <div>
        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
          Address (Permanent / Residential)
        </label>
        <div className="relative">
          <MapPin
            size={15}
            className="absolute left-3.5 top-3 text-slate-400"
          />
          <textarea
            id="input-guest-address"
            rows={2}
            value={address}
            onChange={(e) => onAddressChange(e.target.value)}
            placeholder="Flat/House No., Street, City, State, Pincode"
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm font-medium text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none resize-none"
          />
        </div>
      </div>

      {/* Row 3: ID Proof Type, Number & Purpose of Stay */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* ID Proof Type */}
        <div>
          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
            ID Proof Type{' '}
            {isMandatoryForCheckIn ? (
              <span className="text-red-500">*</span>
            ) : (
              <span className="text-slate-400 text-[10px] font-normal">(Optional for tentative)</span>
            )}
          </label>
          <select
            id="select-id-proof-type"
            value={idProofType}
            onChange={(e) => onIdProofTypeChange(e.target.value)}
            className="w-full h-10 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 text-sm font-medium text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
            required={isMandatoryForCheckIn}
          >
            <option value="">Select ID Type</option>
            {ID_PROOF_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* ID Proof Number */}
        <div>
          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
            ID Proof Number{' '}
            {isMandatoryForCheckIn ? (
              <span className="text-red-500">*</span>
            ) : (
              <span className="text-slate-400 text-[10px] font-normal">(Optional for tentative)</span>
            )}
          </label>
          <div className="relative">
            <CreditCard
              size={15}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              id="input-id-proof-number"
              value={idProofNumber}
              onChange={(e) => onIdProofNumberChange(e.target.value)}
              placeholder="e.g. 1234 5678 9012"
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm font-medium text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
              required={isMandatoryForCheckIn}
            />
          </div>
        </div>

        {/* Purpose of Stay */}
        <div>
          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
            Purpose of Stay
          </label>
          <div className="relative">
            <Compass
              size={15}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <select
              id="select-purpose-of-stay"
              value={purposeOfStay}
              onChange={(e) => onPurposeOfStayChange(e.target.value)}
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm font-medium text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
            >
              <option value="">Select Purpose</option>
              {PURPOSE_OF_STAY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Row 4: ID Document Upload & Preview */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
            ID Document Upload (Image or PDF){' '}
            {isMandatoryForCheckIn ? (
              <span className="text-red-500">*</span>
            ) : (
              <span className="text-slate-400 text-[10px] font-normal">(Optional for tentative)</span>
            )}
          </label>
          <span className="text-[10px] text-slate-400">
            Saved securely in guest-documents storage bucket
          </span>
        </div>

        {/* Hidden inputs for file and mobile camera capture */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={handleFileChange}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFileChange}
        />

        {/* If document already exists or a new file is chosen */}
        {selectedFile || existingDocUrl ? (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 gap-3">
            <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto">
              {docPreviewUrl && (
                <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 overflow-hidden shrink-0 border border-slate-200 dark:border-slate-700 flex items-center justify-center">
                  {selectedFile?.type === 'application/pdf' ||
                  existingDocUrl?.endsWith('.pdf') ? (
                    <FileText size={22} className="text-rose-500" />
                  ) : (
                    <img
                      src={docPreviewUrl}
                      alt="ID Preview"
                      className="w-full h-full object-cover"
                      onError={() => setDocPreviewUrl(null)}
                    />
                  )}
                </div>
              )}

              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate flex items-center gap-1.5">
                  <FileCheck size={14} className="text-emerald-500 shrink-0" />
                  <span className="truncate">
                    {selectedFile
                      ? selectedFile.name
                      : `Existing Document (${existingDocUrl?.split('/').pop() || 'ID Proof'})`}
                  </span>
                </p>
                <p className="text-[11px] text-slate-400">
                  {selectedFile
                    ? `${(selectedFile.size / 1024).toFixed(1)} KB (Ready to upload)`
                    : 'Saved on guest profile'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 shrink-0 w-full sm:w-auto sm:justify-end">
              {docPreviewUrl && (
                <a
                  href={docPreviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                >
                  <ExternalLink size={12} /> View
                </a>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/60 transition-colors flex items-center gap-1"
              >
                <RefreshCw size={12} /> Replace
              </button>
              {selectedFile && (
                <button
                  type="button"
                  onClick={() => onFileSelect(null)}
                  className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 transition-colors"
                  title="Remove selected file"
                >
                  <X size={15} />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="h-14 px-3 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 hover:border-purple-500 dark:hover:border-purple-500 bg-white dark:bg-slate-950 text-slate-600 dark:text-slate-300 text-xs font-medium flex items-center justify-center gap-2 hover:bg-purple-50/50 dark:hover:bg-purple-950/30 transition-colors"
            >
              <Upload size={16} className="text-purple-600 dark:text-purple-400" />
              <span>Choose Image or PDF Document</span>
            </button>

            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              className="h-14 px-3 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 hover:border-purple-500 dark:hover:border-purple-500 bg-white dark:bg-slate-950 text-slate-600 dark:text-slate-300 text-xs font-medium flex items-center justify-center gap-2 hover:bg-purple-50/50 dark:hover:bg-purple-950/30 transition-colors"
            >
              <Camera size={16} className="text-purple-600 dark:text-purple-400" />
              <span>Capture ID with Camera</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
